<template>
  <base-modal-ex :active.sync="isActive"
    :animation-in="animationIn"
    :animation-out="animationOut"
    :animation-duration="animationDuration"
    :can-cancel="false"
    :min-width="responsiveMinWidth"
    has-modal-card>
    <header class="modal-card-head">
      <p class="modal-card-title"
        :class="{ 'is-flex-items-center' : showIcon }">
        <span v-if="showIcon"
          class="icon is-medium"
          :class="`has-text-${iconType}`"
          style="margin-right: 0.5rem">
          <i class="fqi fqi-butterfly fqi-32px" />
        </span>
        <span>Butterfly Search</span>
      </p>
    </header>
    <section class="modal-card-body">
      <!-- No `sortable`: there is nowhere to persist an order on a filter-only screen. -->
      <vehicle-butterfly-selector ref="vehicleButterflySelector"
        v-model="selectedItems" />
    </section>
    <footer class="modal-card-foot">
      <div style="width: 100%; display: flex; justify-content: space-between;">
        <div align="right"
          style="width: 100%">
          <button class="button tooltip"
            @click="reset"
            data-tooltip="Reset">
            <span class="icon"><i class="mdi mdi-refresh mdi-18px" /></span>
            <span>Reset</span>
          </button>
          <button class="button tooltip"
            @click="cancel"
            data-tooltip="Cancel">
            <span class="icon"><i class="mdi mdi-close mdi-18px" /></span>
            <span>Cancel</span>
          </button>
          <button class="button is-primary tooltip"
            @click="done"
            data-tooltip="Done">
            <span class="icon"><i class="mdi mdi-check mdi-18px" /></span>
            <span>Done</span>
          </button>
        </div>
      </div>
    </footer>
  </base-modal-ex>
</template>

<script>
import { BaseModalEx } from '@/components/BulmaModal'
import VehicleButterflySelector from './VehicleButterflySelector.vue'

/**
 * Butterfly picker for screens that only need `butterflyCode` values to filter an
 * available-items grid. Stateless - it loads and saves nothing.
 *
 * v-model is the selector's own selection items, NOT the flat codes the filter is
 * built from. Codes are lossy - they drop the area a part was picked in, and a code
 * such as Decal lives in three areas - so a codes round trip came back with areas and
 * sections the user never picked. Consumers project codes with `selectionItemsToCodes`
 * when they apply the filter, and hand this array straight back on the next open.
 */
export default {
  name: 'VehicleButterflyFilterModal',
  components: {
    BaseModalEx,
    VehicleButterflySelector
  },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    active: {
      type: Boolean,
      default: false
    },
    animationIn: {
      type: String,
      default: 'zoomIn'
    },
    animationOut: {
      type: String,
      default: 'zoomOut'
    },
    animationDuration: {
      type: [String, Number],
      default: 0.3
    },
    showIcon: {
      type: Boolean,
      default: true
    },
    iconType: {
      type: String,
      default: 'primary'
    }
  },
  data() {
    return {
      isActive: this.active,
      selectedItems: this.value.map((item) => ({ ...item })),
      // What to hand back on cancel, so the consumer can restore its previous filter.
      snapshot: this.value.map((item) => ({ ...item })),
      screenWidth: window.innerWidth
    }
  },
  computed: {
    responsiveMinWidth() {
      if (this.screenWidth < 768) {
        return 95 // Mobile
      } else if (this.screenWidth < 1024) {
        return 85 // Tablet
      } else if (this.screenWidth < 1440) {
        return 75 // Desktop
      } else {
        return 65 // Large screens
      }
    }
  },
  watch: {
    active(val) {
      this.isActive = val
    },
    selectedItems: {
      handler(val) {
        this.$emit('input', val)
      },
      deep: true
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    handleResize() {
      this.screenWidth = window.innerWidth
    },
    reset() {
      // Clears the picker only. The filter itself is not applied until Done.
      if (this.$refs.vehicleButterflySelector) {
        this.$refs.vehicleButterflySelector.reset()
      }
      this.selectedItems = []
    },
    cancel() {
      this.$emit('cancel', this.snapshot)
      this.isActive = false
    },
    done() {
      this.$emit('done')
      this.isActive = false
    }
  }
}
</script>

<style scoped>
.is-flex-items-center {
  display: flex;
  align-items: center;
}
</style>
